import { useState } from 'react';
import { Stage, ContentItem, Activity, PainPoint, Opportunity } from '@/types/journey';
import { toast } from "sonner";
import { EmotionLevel } from '@/components/EmotionSelector';

export const useStageOperations = (stages: Stage[], setStages: React.Dispatch<React.SetStateAction<Stage[]>>) => {
  const [editingStage, setEditingStage] = useState<Stage | null>(null);

  const handleAddStage = () => {
    // Create default activity content item
    const defaultActivities: Activity[] = [];
    const defaultPainPoints: PainPoint[] = [];
    const defaultOpportunities: Opportunity[] = [];

    const initialContentItems: ContentItem[] = [
      {
        id: `content-description-${Date.now()}`,
        type: 'description',
        content: ''
      },
      {
        id: `content-activities-${Date.now()}`,
        type: 'activities',
        content: defaultActivities
      },
      {
        id: `content-painPoints-${Date.now()}`,
        type: 'painPoints',
        content: defaultPainPoints
      },
      {
        id: `content-opportunities-${Date.now()}`,
        type: 'opportunities',
        content: defaultOpportunities
      }
    ];

    const newStage: Stage = {
      id: Date.now().toString(),
      title: `Stage ${stages.length + 1}`,
      description: '',
      painPoints: '',
      opportunities: '',
      emotion: 0,
      quote: '',
      contentItems: initialContentItems
    };
    
    setStages([...stages, newStage]);
    toast.success("New stage added");
  };

  const handleUpdateStage = (updatedStage: Stage) => {
    setStages(stages.map(stage => 
      stage.id === updatedStage.id ? updatedStage : stage
    ));
  };

  const handleDeleteStage = (id: string) => {
    toast.warning(
      "Delete stage?", 
      {
        description: "This cannot be undone.",
        action: {
          label: "Delete",
          onClick: () => {
            setStages(stages.filter(stage => stage.id !== id));
            toast.success("Stage deleted");
          }
        }
      }
    );
  };

  const handleEmotionChange = (id: string, emotion: number) => {
    setStages(stages.map(stage => 
      stage.id === id ? { ...stage, emotion } : stage
    ));
  };

  const handleQuoteChange = (id: string, quote: string) => {
    setStages(stages.map(stage => 
      stage.id === id ? { ...stage, quote } : stage
    ));
  };

  // Kept for backward compatibility
  const handleDescriptionChange = (id: string, description: string) => {
    setStages(stages.map(stage => 
      stage.id === id ? { ...stage, description } : stage
    ));
  };

  const handlePainPointsChange = (id: string, painPoints: string) => {
    setStages(stages.map(stage => 
      stage.id === id ? { ...stage, painPoints } : stage
    ));
  };

  const handleOpportunitiesChange = (id: string, opportunities: string) => {
    setStages(stages.map(stage => 
      stage.id === id ? { ...stage, opportunities } : stage
    ));
  };

  const handleTitleChange = (id: string, title: string) => {
    setStages(stages.map(stage => 
      stage.id === id ? { ...stage, title } : stage
    ));
  };

  const handleContentUpdate = (id: string, contentItems: ContentItem[]) => {
    setStages(stages.map(stage => 
      stage.id === id ? { ...stage, contentItems } : stage
    ));
    
    // Update legacy fields for backward compatibility if possible
    const painPointsItem = contentItems.find(item => item.type === 'painPoints');
    const opportunitiesItem = contentItems.find(item => item.type === 'opportunities');
    
    if (painPointsItem || opportunitiesItem) {
      setStages(prevStages => 
        prevStages.map(stage => {
          if (stage.id === id) {
            // Convert pain points array to string if needed
            const painPointsString = painPointsItem && Array.isArray(painPointsItem.content) 
              ? painPointsItem.content.map((pp: PainPoint) => pp.text).join('\n')
              : painPointsItem?.content || stage.painPoints;
            
            // Convert opportunities array to string if needed
            const opportunitiesString = opportunitiesItem && Array.isArray(opportunitiesItem.content)
              ? opportunitiesItem.content.map((opp: Opportunity) => opp.text).join('\n')
              : opportunitiesItem?.content || stage.opportunities;
              
            return {
              ...stage,
              painPoints: painPointsString,
              opportunities: opportunitiesString
            };
          }
          return stage;
        })
      );
    }
  };

  return {
    editingStage,
    setEditingStage,
    handleAddStage,
    handleUpdateStage,
    handleDeleteStage,
    handleEmotionChange,
    handleQuoteChange,
    handleDescriptionChange,
    handlePainPointsChange,
    handleOpportunitiesChange,
    handleTitleChange,
    handleContentUpdate,
  };
};
